/**
 * DOCUMENTATION:
 * Component: CleanCodeFundamentals
 * Purpose: Introduction to the core philosophies of Clean Code.
 * * * Note for Junior Developers:
 * - Technical Debt: The cost of choosing an easy, messy solution now vs. a clean one that takes longer.
 * Like financial debt, it collects "interest" in the form of bugs and slow development later.
 * - Clean code should read like well-written prose.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Fab, Zoom, Tooltip, Stack,
} from "@mui/material";

import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    fontFamily: "'Fira Code', monospace",
});

export default function CleanCodeFundamentals() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const corePhilosophies = useMemo(() => [
        { concept: "Readability over Cleverness", description: "Don't write 'clever' one-liners that no one can understand. Write code that looks obvious." },
        { concept: "The Boy Scout Rule", description: "Always check in code that is slightly cleaner than when you checked it out." },
        { concept: "Technical Debt", description: "Choosing a messy shortcut now creates a 'debt' that must be paid back with interest (extra work) later." },
        { concept: "KISS", description: "Keep It Simple, Stupid. Most systems work best if they are kept simple rather than made complicated." },
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. The Cost of Cleverness",
            code: `// ❌ CLEVER (BUT BAD): Hard to read, hard to debug\nconst d = (a, b) => a.map(x => b.filter(y => y.id === x.userId)[0]);\n\n// ✅ CLEAN: Readable, obvious intent, easy to maintain\nconst mapUsersToPosts = (users, posts) => {\n  return users.map(user => {\n    return posts.find(post => post.userId === user.id);\n  });\n};`,
            desc: "Clever code strokes the ego; clean code helps the team. Always prioritize the person who has to read your code six months from now."
        },
        {
            title: "2. Meaningful Intent",
            code: `// ❌ MESSY: What is 86400000?\nsetTimeout(blastOff, 86400000);\n\n// ✅ CLEAN: Self-documenting code\nconst MILLISECONDS_IN_A_DAY = 86_400_000;\nsetTimeout(blastOff, MILLISECONDS_IN_A_DAY);`,
            desc: "Avoid 'Magic Numbers'. Give them a named constant so the next developer doesn't have to pull out a calculator to understand your logic."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    THE CLEAN CODE MINDSET
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            What is Clean Code?
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Clean code is code that has been taken care of. Someone has taken the time to keep it simple and orderly. They have paid attention to detail. They have cared.
                            <strong> Bjarne Stroustrup</strong> (inventor of C++) says: "I like my code to be elegant and efficient. The logic should be straightforward to make it hard for bugs to hide."
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>Core Philosophies</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            {corePhilosophies.map((item, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", height: "100%" }}>
                                        <Typography variant="subtitle1" sx={{ color: "goldenrod", fontWeight: 700 }}>{item.concept}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>{item.description}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                    {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                </Button>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/clean-code-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "goldenrod", color: "#100720", fontWeight: 800, px: 4, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
